import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";
import TryItButton from "../../../TryItButton";
import OutputBox from "../../../OutputBox";

export default function JS_Function_Call() {
  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">
        📞 JavaScript <strong style={{ color: "red" }}>Function.call()</strong>{" "}
        Method
      </h1>

      <p className="tutorial-intro">
        The <code className="highlight_code">call()</code> method allows you to
        <strong>invoke a function immediately</strong> and explicitly set the{" "}
        <code className="highlight_code">this</code> value. It’s useful for
        borrowing methods or calling functions with a specific context.
      </p>

      {/* 🧠 Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem 2rem",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🧠 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            <code className="highlight_code">call()</code> invokes a function
            immediately with a specified{" "}
            <code className="highlight_code">this</code> value.
          </li>
          <li>Extra parameters are passed as individual arguments.</li>
          <li>Used for method borrowing or controlling context.</li>
          <li>
            Difference from <code className="highlight_code">apply()</code>:
            call → arguments list, apply → array.
          </li>
        </ul>
      </div>

      {/* 1️⃣ Basic Example */}
      <div className="tutorial-section">
        <h3>
          1️⃣ Basic <code className="highlight_code">call()</code> Example
        </h3>
        <p>
          You can use <code className="highlight_code">call()</code> to invoke a
          function and pass in a <code className="highlight_code">this</code>{" "}
          value.
        </p>

        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`function greet() {
  console.log("Hello, " + this.name);
}

const person = { name: "Alice" };

greet.call(person);`}
          />
          <TryItButton
            code={`function greet(){console.log("Hello, "+this.name);}const person={name:"Alice"};greet.call(person);`}
          />
        </div>
        <OutputBox>Output: Hello, Alice</OutputBox>
      </div>

      {/* 2️⃣ Passing Arguments */}
      <div className="tutorial-section">
        <h3>
          2️⃣ Passing Arguments with{" "}
          <code className="highlight_code">call()</code>
        </h3>
        <p>
          You can pass additional arguments after the first one (the{" "}
          <code className="highlight_code">this</code> value).
        </p>

        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`function introduce(greeting, punctuation) {
  console.log(greeting + ", I'm " + this.name + punctuation);
}

const user = { name: "Bob" };

introduce.call(user, "Hi", "!");`}
          />
          <TryItButton
            code={`function introduce(greeting,punctuation){console.log(greeting+", I'm "+this.name+punctuation);}const user={name:"Bob"};introduce.call(user,"Hi","!");`}
          />
        </div>
        <OutputBox>Output: Hi, I'm Bob!</OutputBox>
      </div>

      {/* 3️⃣ Method Borrowing */}
      <div className="tutorial-section">
        <h3>
          3️⃣ Borrowing Methods with{" "}
          <code className="highlight_code">call()</code>
        </h3>
        <p>
          You can borrow a method from one object and use it with another
          object.
        </p>

        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`const person1 = {
  name: "Charlie",
  greet: function() {
    console.log("Hello, " + this.name);
  }
};

const person2 = { name: "David" };

person1.greet.call(person2);`}
          />
          <TryItButton
            code={`const person1={name:"Charlie",greet:function(){console.log("Hello, "+this.name);}};const person2={name:"David"};person1.greet.call(person2);`}
          />
        </div>
        <OutputBox>Output: Hello, David</OutputBox>
      </div>

      {/* 4️⃣ call() with Built-in Functions */}
      <div className="tutorial-section">
        <h3>
          4️⃣ Using <code className="highlight_code">call()</code> with Built-in
          Functions
        </h3>
        <p>
          The <code className="highlight_code">call()</code> method can be used
          with built-in JavaScript methods like{" "}
          <code className="highlight_code">Math.max()</code>.
        </p>

        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`console.log(Math.max.call(null, 10, 20, 30));`}
          />
          <TryItButton code={`console.log(Math.max.call(null,10,20,30));`} />
        </div>
        <OutputBox>Output: 30</OutputBox>
      </div>

      {/* 5️⃣ call() vs apply() */}
      <div className="tutorial-section">
        <h3>
          5️⃣ Difference Between <code className="highlight_code">call()</code>{" "}
          and <code className="highlight_code">apply()</code>
        </h3>
        <p>
          <code className="highlight_code">call()</code> accepts a list of
          arguments, while <code className="highlight_code">apply()</code>{" "}
          accepts an array of arguments.
        </p>

        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`function sum(a, b) {
  return a + b;
}

console.log(sum.call(null, 5, 10)); // Arguments listed
console.log(sum.apply(null, [5, 10])); // Arguments in array`}
          />
          <TryItButton
            code={`function sum(a,b){return a+b;}console.log(sum.call(null,5,10));console.log(sum.apply(null,[5,10]));`}
          />
        </div>
        <OutputBox>
          Output:
          <pre>
            {`15
15`}
          </pre>
        </OutputBox>
      </div>

      {/* 🚀 Next Step */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          padding: "1rem 2rem",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Step</h2>
        <p>
          Learn about <strong>Function.apply()</strong> next — it’s similar to{" "}
          <code className="highlight_code">call()</code> but takes an array of
          arguments instead.
        </p>
      </div>
    </div>
  );
}
